/*eslint complexity: ["error", 26]*/

import React, { useState } from 'react';

import { Grid } from '../../../common/components/Grid/Grid';
import { GridColumn } from '../../../common/components/Grid/GridDataTypes';
import {
  GetDictionaryDto,
  GetEstimateItemDto,
  ProfileSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import numberFormatter from '../../../common/numberFormatter';
import momencik from '../../../common/momencik';
import {
  CurrencyIsoTypeEnum,
  PositionTypeEnum,
} from '../../../common/pokCore/validation/enums';
import mathUtils from '../../../utils/mathUtils';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { EstimateItemFieldsDefinition } from '../../../common/pokCore/validation/estimateItemFields.definition';

import { OrderEstimateItemsGridMonthsSum } from './OrderEstimateItemsGridMonthsSum';

interface OrderEstimateItemsGridProps {
  data: GetEstimateItemDto[];
  currencyCode?: string;
}

const getItemsGridSummary = (
  data: GetEstimateItemDto[],
  keys: string[],
  currencyCode?: string,
) => {
  data = data.filter(({ id }) => keys.includes(id));
  return {
    key: 'summary',
    values: {
      'estimateItem.comment': 'Suma',
      'estimateItem.purchaseNetTotal': numberFormatter(
        mathUtils.add(
          ...data.map(({ purchaseNetTotal }) =>
            mathUtils.round(purchaseNetTotal),
          ),
        ),
        true,
        (currencyCode as CurrencyIsoTypeEnum) || undefined,
      ),
    },
    tdCssClass: {
      'estimateItem.purchaseNetTotal': 'amounts-align-right',
    },
  };
};

export const buildParametersString = (
  estimateItem: GetEstimateItemDto,
  lang: string,
) => {
  const result: { label: string; value: string }[] = [];
  const { schema } = estimateItem.estimateByMonth.profile;

  const schemaKeysToGet = EstimateItemFieldsDefinition.parametersFields.filter(
    field => {
      switch (estimateItem.positionType) {
        case PositionTypeEnum.Standard:
          return field.standard;
        case PositionTypeEnum.Commission:
          return field.commission;
        case PositionTypeEnum.TransferredCommission:
          return field.transferredCommission;
        case PositionTypeEnum.TechnicalCost:
          return field.technicalCost;
      }
    },
  );

  //wyjątek dla actionGroup - nie wrzucamy go dla pozycji innych niż standard
  if (estimateItem?.positionType !== PositionTypeEnum.Standard) {
    const index = schemaKeysToGet.findIndex(key => key.name === 'actionGroup');
    schemaKeysToGet.splice(index, 1);
  }

  schemaKeysToGet.forEach(key => {
    if (
      schema[key.name as keyof ProfileSchemaDto] &&
      estimateItem[key.name as keyof GetEstimateItemDto]
    ) {
      if (
        typeof estimateItem[key.name as keyof GetEstimateItemDto] === 'string'
      ) {
        return result.push({
          label:
            lang === 'PL'
              ? schema[key.name as keyof ProfileSchemaDto]?.displayNamePl || ''
              : schema[key.name as keyof ProfileSchemaDto]?.displayNameEn || '',
          value:
            (estimateItem[key.name as keyof GetEstimateItemDto] as string) ||
            '',
        });
      }

      if (
        typeof estimateItem[key.name as keyof GetEstimateItemDto] === 'boolean'
      ) {
        return result.push({
          label:
            lang === 'PL'
              ? schema[key.name as keyof ProfileSchemaDto]?.displayNamePl || ''
              : schema[key.name as keyof ProfileSchemaDto]?.displayNameEn || '',
          value: estimateItem[key.name as keyof GetEstimateItemDto]
            ? lang === 'PL'
              ? 'TAK'
              : 'YES'
            : lang === 'PL'
              ? 'NIE'
              : 'NO',
        });
      }

      return result.push({
        label:
          lang === 'PL'
            ? schema[key.name as keyof ProfileSchemaDto]?.displayNamePl || ''
            : schema[key.name as keyof ProfileSchemaDto]?.displayNameEn || '',
        value:
          (
            estimateItem[
              key.name as keyof GetEstimateItemDto
            ] as GetDictionaryDto
          )?.value || '',
      });
    }
  });

  return (
    <ul className="m-0 list-group-numbered p-0">
      {result.map(({ label, value }, index) => (
        <li key={index}>
          {label}: {value}
        </li>
      ))}
    </ul>
  );
};

export const OrderEstimateItemsGrid: React.FC<
  OrderEstimateItemsGridProps
> = props => {
  const { t, tk } = useTranslation('projectLng');
  const [staticDataKeys, setStaticDataKeys] = useState<string[]>([]);
  const pok = usePokCore();
  const columns: GridColumn[] = [
    { header: t(tk.common.name), property: 'position.name' },
    { header: t(tk.common.month), property: 'estimateByMonth.date' },
    { header: t(tk.common.client), property: 'client.name' },
    {
      header: t(tk.estimates.positionCountry),
      property: 'countryDictionary.value',
    },
    {
      header: t(tk.estimates.parameters),
      property: 'parameters',
      noSorting: true,
    },
    { header: t(tk.estimates.comment), property: 'estimateItem.comment' },
    {
      header: t(tk.estimates.purchaseAmount),
      property: 'estimateItem.purchaseNetTotal',
    },
  ];

  const getDictionaryPlEnValue = (dictionary?: GetDictionaryDto) =>
    pok.getLanguage() === 'PL' ? dictionary?.value : dictionary?.valueEn;

  const data = props.data.map(estimateItem => {
    return {
      key: estimateItem.id,
      values: {
        'position.name': estimateItem.position.name,
        'estimateByMonth.date': momencik(
          estimateItem.estimateByMonth.date,
          'YYYY-MM',
        ),
        'countryDictionary.value': getDictionaryPlEnValue(
          estimateItem.countryDictionary,
        ),
        'client.name':
          estimateItem.position.client.shortname ||
          estimateItem.position.client.name,
        'estimateItem.purchaseNetTotal': numberFormatter(
          estimateItem.purchaseNetTotal || '0',
          true,
          (props.currencyCode as CurrencyIsoTypeEnum) || undefined,
        ),
        parameters: buildParametersString(estimateItem, pok.getLanguage()),
        'estimateItem.comment': estimateItem.comment,
      },
      orderByStringValues: {
        name: estimateItem.position.name,
      },
      orderByNumberValues: {
        'estimateItem.purchaseNetTotal': mathUtils.parse(
          estimateItem.purchaseNetTotal || '0',
        ),
      },
      tdCssClass: {
        'estimateItem.purchaseNetTotal': 'amounts-align-right',
      },
    };
  });

  const setFilteredRowKeys = (keys: string[]) => {
    if (JSON.stringify(staticDataKeys) !== JSON.stringify(keys)) {
      setStaticDataKeys(keys);
    }
  };

  return (
    <>
      <Grid
        data={data}
        columns={columns}
        initialOrderColumn="estimateByMonth.date"
        initialOrderDirection="DESC"
        availablePageSizes={[10, 20, 50]}
        emptyGridMessage={t(tk.estimates.noPositionsAssigned)}
        summaryData={getItemsGridSummary(
          props.data,
          staticDataKeys,
          props.currencyCode,
        )}
        filteredStaticRowKeys={setFilteredRowKeys}
      />
      <OrderEstimateItemsGridMonthsSum
        data={props.data}
        currencyCode={props.currencyCode}
      />
    </>
  );
};
